/**
 * Menu definitions for navigation system
 * Consolidated menu structure used by both static and dynamic navigation
 */

import { OrgType } from '@/lib/types/org';

export interface MenuItem {
  id: string;
  label: string;
  path: string;
  icon?: string;
  requiredModules?: string[];
  availableFor?: OrgType[] | 'all';
  openInNewWindow?: boolean;
  featureFlags?: string[];
}

export interface MenuGroup {
  group: string;
  items: MenuItem[];
}

export interface TopMenuItem {
  id: string;
  label: string;
  path: string;
  requiredModules?: string[];
  availableFor?: OrgType[] | 'all';
  openInNewWindow?: boolean;
  featureFlags?: string[];
}

// Left menu groups definition
export const LEFT_MENU_GROUPS: MenuGroup[] = [
  {
    group: 'Home',
    items: [
      { 
        id: 'home', 
        icon: 'Home', 
        label: 'Home', 
        path: '/home', 
        requiredModules: [], 
        availableFor: 'all' 
      }
    ]
  },
  {
    group: 'Main',
    items: [
      {
        id: 'masters',
        icon: 'Package',
        label: 'Masters',
        path: '/brands',
        requiredModules: ['catalog_management.products', 'catalog_management.categories', 'catalog_management.brands'],
        availableFor: ['manufacturer']
      },
      {
        id: 'products',
        icon: 'Package',
        label: 'Products',
        path: '/products',
        requiredModules: ['catalog_management.products'],
        availableFor: ['manufacturer']
      },
      {
        id: 'stock',
        icon: 'Warehouse',
        label: 'Stock',
        path: '/stock/in',
        requiredModules: ['inventory_management.stock_ops'],
        availableFor: ['manufacturer']
      },
      {
        id: 'promotions',
        icon: 'Archive',
        label: 'Offers',
        path: '/prom',
        requiredModules: ['sales_management.orders'],
        availableFor: ['retailer', 'internal']
      },
      {
        id: 'scan',
        icon: 'Archive',
        label: 'Scan',
        path: '/scan',
        requiredModules: ['catalog_management.products', 'inventory_management.stock_ops'],
        availableFor: ['retailer']
      },
      {
        id: 'orders',
        icon: 'ShoppingCart',
        label: 'Orders',
        path: '/sales-orders',
        requiredModules: ['sales_management.orders', 'purchase_management.orders'],
        availableFor: ['manufacturer']
      }
    ]
  },
  {
    group: 'People',
    items: [
      {
        id: 'people',
        icon: 'Users',
        label: 'People',
        path: '/customers',
        requiredModules: ['sales_management.customers'],
        availableFor: ['manufacturer']
      }
    ]
  },
  {
    group: 'Insights',
    items: [
      {
        id: 'analytics',
        icon: 'BarChart3',
        label: 'Reports',
        path: '/reports',
        requiredModules: ['reporting.purchase', 'reporting.sales', 'reporting.inventory'],
        availableFor: ['manufacturer'],
        openInNewWindow: true
      }
    ]
  },
  {
    group: 'Settings',
    items: [
      {
        id: 'settings',
        icon: 'Settings',
        label: 'Settings',
        path: '/settings',
        requiredModules: ['user_management.organizations'],
        availableFor: ['internal']
      }
    ]
  }
];

// Top menu (sub-menus) definition
export const TOP_MENUS: Record<string, TopMenuItem[]> = {
  home: [],
  
  masters: [
    { 
      id: 'brands', 
      label: 'Brand', 
      path: '/brands', 
      requiredModules: ['catalog_management.brands'], 
      availableFor: 'all' 
    },
    { 
      id: 'colors', 
      label: 'Color', 
      path: '/colors', 
      requiredModules: ['catalog_management.products'], 
      availableFor: 'all' 
    },
    { 
      id: 'sizes', 
      label: 'Size', 
      path: '/sizes', 
      requiredModules: ['catalog_management.products'], 
      availableFor: 'all' 
    },
    { 
      id: 'warehouses', 
      label: 'Warehouses', 
      path: '/warehouses', 
      requiredModules: ['inventory_management.warehouses'], 
      availableFor: 'all' 
    }
  ],
  
  products: [
    { 
      id: 'categories', 
      label: 'Categories', 
      path: '/categories', 
      requiredModules: ['catalog_management.categories'], 
      availableFor: ['manufacturer'] 
    },
    { 
      id: 'catalog', 
      label: 'Article', 
      path: '/products', 
      requiredModules: ['catalog_management.products'], 
      availableFor: ['manufacturer'] 
    },
    { 
      id: 'cartons', 
      label: 'Cartons', 
      path: '/cartons', 
      requiredModules: ['catalog_management.products'], 
      availableFor: ['manufacturer'] 
    }
  ],
  
  stock: [
    { 
      id: 'stock-in', 
      label: 'Stock In', 
      path: '/stock/in', 
      requiredModules: ['inventory_management.stock_ops'], 
      availableFor: ['manufacturer'] 
    },
    { 
      id: 'stock-out', 
      label: 'Stock Out', 
      path: '/stock/out', 
      requiredModules: ['inventory_management.stock_ops'], 
      availableFor: ['manufacturer'] 
    },
    { 
      id: 'wt', 
      label: 'Transfer Gatepass', 
      path: '/warehouse-transfer', 
      requiredModules: ['inventory_management.warehouses'], 
      availableFor: ['manufacturer'] 
    }
  ],
  
  orders: [
    { 
      id: 'sales-orders', 
      label: 'Sales Orders', 
      path: '/sales-orders', 
      requiredModules: ['sales_management.orders'], 
      availableFor: ['manufacturer'] 
    },
    { 
      id: 'purchase-orders', 
      label: 'Purchase Orders', 
      path: '/po', 
      requiredModules: ['purchase_management.orders'], 
      availableFor: ['manufacturer'] 
    }
  ],
  
  people: [
    { 
      id: 'customers', 
      label: 'Customers', 
      path: '/customers', 
      requiredModules: ['sales_management.customers'], 
      availableFor: ['manufacturer'] 
    },
    { 
      id: 'suppliers', 
      label: 'Suppliers', 
      path: '/sup', 
      requiredModules: ['vendor_management.suppliers'], 
      availableFor: ['manufacturer'] 
    },
    { 
      id: 'vendors', 
      label: 'Vendors', 
      path: '/vend', 
      requiredModules: ['vendor_management.suppliers'], 
      availableFor: ['manufacturer'] 
    }
  ],
  
  analytics: [],
  
  settings: [
    { 
      id: 'general', 
      label: 'General', 
      path: '/settings/general', 
      requiredModules: ['user_management.organizations'], 
      availableFor: ['internal'] 
    },
    { 
      id: 'users', 
      label: 'Users & Permissions', 
      path: '/settings/users', 
      requiredModules: ['user_management.users'], 
      availableFor: ['internal'] 
    }
  ]
};

// URL to module mapping for route permissions
export const URL_MODULE_MAPPING: Record<string, string[]> = {
  // Home and overview pages
  '/home': [],
  '/overview': [],
  '/dashboard': [],
  
  // Master data pages
  '/brands': ['catalog_management.brands'],
  '/colors': ['catalog_management.products'],
  '/sizes': ['catalog_management.products'],
  '/warehouses': ['inventory_management.warehouses'],
  '/categories': ['catalog_management.categories'],

  // Product management
  '/products': ['catalog_management.products'],
  '/cartons': ['catalog_management.products'],

  // Inventory management
  '/stock': ['inventory_management.stock_ops'],
  '/stock/in': ['inventory_management.stock_ops'],
  '/stock/out': ['inventory_management.stock_ops'],
  '/warehouse-transfer': ['inventory_management.warehouses'],

  // Order management
  '/sales-orders': ['sales_management.orders'],
  '/po': ['purchase_management.orders'],

  // Customer and supplier management
  '/customers': ['sales_management.customers'],
  '/sup': ['vendor_management.suppliers'],
  '/vend': ['vendor_management.suppliers'],

  // Analytics and reporting
  '/reports': ['reporting.purchase', 'reporting.sales', 'reporting.inventory'],

  // Settings and administration
  '/settings': ['user_management.organizations'],
  '/settings/general': ['user_management.organizations'],
  '/settings/users': ['user_management.users']
};

// Helper functions
export function getMenuItemById(id: string): MenuItem | undefined {
  for (const group of LEFT_MENU_GROUPS) {
    const item = group.items.find(item => item.id === id);
    if (item) return item;
  }
  return undefined;
}

export function getTopMenuItems(menuId: string): TopMenuItem[] {
  return TOP_MENUS[menuId] || [];
}

export function getRequiredModulesForUrl(pathname: string): string[] {
  // Try exact match first
  const exactMatch = URL_MODULE_MAPPING[pathname];
  if (exactMatch) return exactMatch;
  
  // Try prefix matching for nested routes
  for (const [path, modules] of Object.entries(URL_MODULE_MAPPING)) {
    if (path !== '/' && pathname.startsWith(path + '/')) {
      return modules;
    }
  }
  
  return [];
}

export function isPublicUrl(pathname: string): boolean {
  const publicPaths = ['/home', '/login', '/error', '/auth', '/'];
  return publicPaths.some(path => pathname === path || pathname.startsWith(path + '/'));
}
